import { useEffect, useState, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import "./App.css";

// Pages
import LoginPage from "./pages/LoginPage";
import Dashboard from "./pages/Dashboard";
import ClientsPage from "./pages/ClientsPage";
import CalendarPage from "./pages/CalendarPage";
import TeamPage from "./pages/TeamPage";
import SocialPage from "./pages/SocialPage";
import SettingsPage from "./pages/SettingsPage";
import TaskDetailPage from "./pages/TaskDetailPage";
import ReportsPage from "./pages/ReportsPage";
import TasksPage from "./pages/TasksPage";
import StatsPage from "./pages/StatsPage";
import SchedaCliente from "./pages/SchedaCliente";
import NotificationsPage from "./pages/NotificationsPage";
import CompensationPage from "./pages/CompensationPage";
import CrmPage from "./pages/CrmPage";
import ContrattiPage from "./pages/ContrattiPage";
import AreaCommercialePage from "./pages/AreaCommercialePage";
import FirmaContrattoPage from "./pages/FirmaContrattoPage";

// Components
import Sidebar from "./components/Sidebar";
import { ShaderBackground } from "./components/ui/blue-waves";
import MobileNav from "./components/MobileNav";
import ClientiNonGestitiFab from "./components/ClientiNonGestitiFab";
import LocalBadge from "./components/LocalBadge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// L'URL del backend arriva sempre da REACT_APP_BACKEND_URL: nessun valore
// scritto fisso, altrimenti il bundle resta legato a un ambiente.

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const search = new URLSearchParams(window.location.search);

      // Login Google diretto: il backend rimanda un codice usa-e-getta di 5 minuti,
      // non il session_token, che così non finisce nella cronologia del browser.
      const authCodeMatch = hash.match(/auth_code=([^&]+)/);
      const authCode = authCodeMatch ? authCodeMatch[1] : search.get("auth_code");

      // Percorso storico via Emergent, ancora attivo finché il nuovo non è
      // provato con un account reale.
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      // Su mobile, in alcuni casi il fragment può essere URL-encoded come %23
      let sessionId = sessionIdMatch ? sessionIdMatch[1] : null;
      if (!sessionId) {
        // Fallback: alcune integrazioni mobili usano ?session_id
        sessionId = search.get("session_id");
      }

      if (!authCode && !sessionId) {
        navigate("/login", { replace: true });
        return;
      }

      const endpoint = authCode ? "/auth/google/exchange" : "/auth/session";
      const payload = authCode ? { auth_code: authCode } : { session_id: sessionId };

      try {
        // NB: NON usiamo credentials:"include" qui per evitare problemi ITP su iOS Safari/Chrome.
        // Il session_token torna nel body della risposta e viene salvato in localStorage.
        const response = await fetch(`${API}${endpoint}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          mode: "cors",
          body: JSON.stringify(payload)
        });

        if (response.ok) {
          const userData = await response.json();
          if (userData.session_token) {
            localStorage.setItem("session_token", userData.session_token);
          }
          // Pulisce hash e naviga
          window.history.replaceState(null, "", window.location.pathname);
          navigate("/dashboard", { state: { user: userData }, replace: true });
        } else {
          // Estrai detail per dare un messaggio specifico (es. "Sessione Google scaduta")
          let errorMsg = "Accesso non autorizzato. Contatta il Project Manager.";
          try {
            const errBody = await response.json();
            errorMsg = errBody.detail || errorMsg;
          } catch (_) { /* response non-JSON, mantieni default */ }
          // eslint-disable-next-line no-console
          console.error(`[AUTH] ${endpoint} ha risposto ${response.status}: ${errorMsg}`);
          navigate("/login", { state: { error: `${errorMsg} (HTTP ${response.status})` }, replace: true });
        }
      } catch (error) {
        // Errore di rete/CORS/timeout — mostra dettagli per debug mobile
        // eslint-disable-next-line no-console
        console.error(`[AUTH] Errore fetch ${endpoint}:`, error);
        const detail = error?.message || String(error);
        navigate("/login", {
          state: { error: `Errore di rete durante l'autenticazione: ${detail}. Riprova o controlla la connessione.` },
          replace: true,
        });
      }
    };

    processAuth();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="animate-pulse text-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-muted-foreground">Autenticazione in corso...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const location = useLocation();

  useEffect(() => {
    // If user data is passed from AuthCallback, use it directly
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      try {
        // Bearer-only (no credentials:include) per compatibilità iOS Safari/Chrome
        const token = localStorage.getItem("session_token");
        if (!token) {
          setIsAuthenticated(false);
          return;
        }
        const response = await fetch(`${API}/auth/me`, {
          headers: { "Authorization": `Bearer ${token}` }
        });

        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
          setIsAuthenticated(true);
        } else {
          // Clear invalid token
          localStorage.removeItem("session_token");
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error("Auth check error:", error);
        localStorage.removeItem("session_token");
        setIsAuthenticated(false);
      }
    };

    checkAuth();
  }, [location.state]);

  // Loading state
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Caricamento...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children({ user, setUser });
};

// Main Layout with Sidebar/Mobile Nav
const MainLayout = ({ children, user, setUser }) => {
  // Lo shader WebGL sta dietro a tutto: `isolate` crea il contesto di
  // impilamento che permette al canvas con -z-10 di stare sopra lo sfondo
  // CSS del contenitore (fallback senza WebGL) ma sotto i contenuti.
  return (
    <div className="pipo-gradient relative isolate h-dvh overflow-hidden">
      <ShaderBackground className="pointer-events-none fixed inset-0 -z-10" />

      {/* Desktop Sidebar */}
      <div className="hidden lg:block">
        <Sidebar user={user} setUser={setUser} />
      </div>
      
      {/* Main Content */}
      <main className="lg:ml-64 h-dvh overflow-y-auto overscroll-contain bg-transparent pb-20 lg:pb-0">
        <div className="p-4 lg:p-8">
          {children}
        </div>
      </main>
      
      {/* Mobile Bottom Nav */}
      <div className="lg:hidden">
        <MobileNav user={user} setUser={setUser} />
      </div>

      <ClientiNonGestitiFab />
    </div>
  );
};

// App Router
const AppRouter = () => {
  const location = useLocation();

  // Check for session_id (percorso Emergent) o auth_code (login Google diretto)
  // nel fragment dell'URL, sincronamente prima del routing normale.
  if (location.hash?.includes("session_id=") || location.hash?.includes("auth_code=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* Pubblica, nessuna autenticazione: il cliente la apre dal link ricevuto via email. */}
      <Route path="/contratto/firma/:token" element={<FirmaContrattoPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            {({ user, setUser }) => (
              <MainLayout user={user} setUser={setUser}>
                {/* Il commerciale non ha task: la sua dashboard è l'Area commerciale.
                    Solo per il ruolo principale, chi lo ha come secondario (es. un
                    PM) resta sulla dashboard del suo ruolo. */}
                {user?.role === "commerciale"
                  ? <Navigate to="/area-commerciale" replace />
                  : <Dashboard user={user} />}
              </MainLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients"
        element={
          <ProtectedRoute>
            {({ user, setUser }) => (
              <MainLayout user={user} setUser={setUser}>
                <ClientsPage user={user} />
              </MainLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/scheda-cliente"
        element={
          <ProtectedRoute>
            {({ user, setUser }) => (
              <MainLayout user={user} setUser={setUser}>
                <SchedaCliente user={user} />
              </MainLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/scheda-cliente/:clientId"
        element={
          <ProtectedRoute>
            {({ user, setUser }) => (
              <MainLayout user={user} setUser={setUser}>
                <SchedaCliente user={user} />
              </MainLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/calendar"
        element={
          <ProtectedRoute>
            {({ user, setUser }) => (
              <MainLayout user={user} setUser={setUser}>
                <CalendarPage user={user} />
              </MainLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/team"
        element={
          <ProtectedRoute>
            {({ user, setUser }) => (
              <MainLayout user={user} setUser={setUser}>
                <TeamPage user={user} />
              </MainLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            {({ user, setUser }) => (
              <MainLayout user={user} setUser={setUser}>
                <ReportsPage user={user} />
              </MainLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifiche"
        element={
          <ProtectedRoute>
            {({ user, setUser }) => (
              <MainLayout user={user} setUser={setUser}>
                <NotificationsPage user={user} />
              </MainLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/social"
        element={
          <ProtectedRoute>
            {({ user, setUser }) => (
              <MainLayout user={user} setUser={setUser}>
                <SocialPage user={user} />
              </MainLayout>
            )}
          </ProtectedRoute>
        }
      />

      <Route
        path="/crm"
        element={
          <ProtectedRoute>
            {({ user, setUser }) => (
              <MainLayout user={user} setUser={setUser}>
                <CrmPage user={user} />
              </MainLayout>
            )}
          </ProtectedRoute>
        }
      />

      <Route
        path="/area-commerciale"
        element={
          <ProtectedRoute>
            {({ user, setUser }) => (
              <MainLayout user={user} setUser={setUser}>
                <AreaCommercialePage user={user} />
              </MainLayout>
            )}
          </ProtectedRoute>
        }
      />

      <Route
        path="/contratti"
        element={
          <ProtectedRoute>
            {({ user, setUser }) => (
              <MainLayout user={user} setUser={setUser}>
                <ContrattiPage key="standard" user={user} />
              </MainLayout>
            )}
          </ProtectedRoute>
        }
      />
      {/* Stessa pagina, catalogo dei servizi eventi. La key forza un montaggio
          nuovo passando da una rotta all'altra: il wizard aperto non deve
          sopravvivere al cambio di catalogo. */}
      <Route
        path="/contratti-eventi"
        element={
          <ProtectedRoute>
            {({ user, setUser }) => (
              <MainLayout user={user} setUser={setUser}>
                <ContrattiPage key="eventi" ambito="eventi" user={user} />
              </MainLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/tasks"
        element={
          <ProtectedRoute>
            {({ user, setUser }) => (
              <MainLayout user={user} setUser={setUser}>
                <TasksPage user={user} />
              </MainLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            {({ user, setUser }) => (
              <MainLayout user={user} setUser={setUser}>
                <SettingsPage user={user} />
              </MainLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/stats"
        element={
          <ProtectedRoute>
            {({ user, setUser }) => (
              <MainLayout user={user} setUser={setUser}>
                <StatsPage user={user} />
              </MainLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/task/:taskId"
        element={
          <ProtectedRoute>
            {({ user, setUser }) => (
              <MainLayout user={user} setUser={setUser}>
                <TaskDetailPage user={user} />
              </MainLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/compensi"
        element={
          <ProtectedRoute>
            {({ user, setUser }) => (
              <MainLayout user={user} setUser={setUser}>
                <CompensationPage user={user} />
              </MainLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AppRouter />
      <LocalBadge />
      <Toaster position="top-right" richColors />
    </BrowserRouter>
  );
}

export default App;
